import React, { useEffect, useState } from "react";
import { getStudentRequestsFromIDB, setStudentRequestsToIDB, clearStudentRequestsCache } from '../../../utils/idbStudentRequests';
import { motion, AnimatePresence } from "framer-motion";
import CustomTable from "../../../shared/custom-table/custom-table";
import { columnsRequestsStudent } from "../../../mocks/fakeData";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Button,
  Modal,
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getAllStudentRequests,
  respondOfferService,
  getAvailableSubjects,
  adjustStudentOfferRequestService,
} from "../../../services/student-offer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCalendarAlt, 
  faCheck, 
  faTimes, 
  faExternalLinkAlt, 
  faFileAlt,
  faUserCircle,
  faCoins,
  faClock,
  faPen
} from "@fortawesome/free-solid-svg-icons";

interface RequestData {
  id: number;
  nom: string;
  offerType: string;
  status: "PENDING" | "ACCEPTED" | "REJECTED";
  createdAt: string;
  startDate: string | null;
  endDate: string | null;
  paymentImageUrl: string;
  subjects: string;
  subjectCount: number;
  totalPrice: string;
  paidAmount?: number;
  baseAmount?: number;
  period: string;
  selectedPeriodCode?: string;
  pen?: any;
  faPen?: any;
}

type TableData = Record<string, any>;

const Requests = () => {
  const CACHE_MAX_AGE = 10 * 60 * 1000; // 10 minutes
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<RequestData | null>(null);
  const [data, setData] = useState<RequestData[]>([]);
  const [originalData, setOriginalData] = useState<RequestData[]>([]);
  const [subjectsList, setSubjectsList] = useState<any[]>([]);
  const [slectedStatus, setSelectedStatus] = useState<"ACCEPTED" | "REJECTED">("ACCEPTED");
  const [confirmAction, setConfirmAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [adjustPayload, setAdjustPayload] = useState<{ startDate: string; endDate: string; status: "ACCEPTED" | "REJECTED"; }>({
    startDate: "",
    endDate: "",
    status: "ACCEPTED",
  });
  const [savingAdjust, setSavingAdjust] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState<"asc" | "desc" | "none">("none");

  const getTodayString = () =>
    new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);

  const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    const directMatch = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (directMatch) {
      return directMatch[1];
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return "";
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const addPeriodToDate = (startDate: string, periodCode?: string) => {
    if (!startDate) return "";
    const monthsToAdd: Record<string, number> = {
      MONTHLY: 1,
      TRIMESTER: 3,
      SEMESTER: 6,
      YEARLY: 12,
    };
    const period = (periodCode || "MONTHLY").toUpperCase();
    const targetMonths = monthsToAdd[period] ?? 1;

    const baseDate = new Date(`${startDate}T00:00:00Z`);
    if (Number.isNaN(baseDate.getTime())) return "";
    const baseDay = baseDate.getUTCDate();
    baseDate.setUTCMonth(baseDate.getUTCMonth() + targetMonths);
    if (baseDate.getUTCDate() !== baseDay) {
      baseDate.setUTCDate(0);
    }
    return baseDate.toISOString().slice(0, 10);
  };

  const fetchData = async () => {
    try {
      // Try to get cached requests from IndexedDB
      const cached = await getStudentRequestsFromIDB();
      const now = Date.now();

      if (cached && Array.isArray(cached.requests) && cached.timestamp && now - cached.timestamp < CACHE_MAX_AGE) {
        setData(cached.requests);
        setOriginalData(cached.requests);
        setLoading(false);
        return;
      }

      setLoading(true);
      let subjectsArr: any[] = [];
      // Always fetch subjects fresh (they may change)
      const subjectsRes = await getAvailableSubjects();
      subjectsArr = Array.isArray(subjectsRes) ? subjectsRes : subjectsRes.data || [];
      setSubjectsList(subjectsArr);

      // If no cache or cache is old, fetch from API
      const res = await getAllStudentRequests();
      console.log('Raw student requests:', res.data);
      const formattedData = res.data.map((item: any) => {
        // Debug: log each item
        console.log('Student request item:', item);
        const subjectCount = item.requestedSubjectsCount ?? item.subjectCount ?? (Array.isArray(item.subjectIds) ? item.subjectIds.length : 0);
        const selectedPeriod = item.selectedPeriod ?? item.period ?? 'MONTHLY';

        // Get the price based on selected period
        let unitPrice = 0;
        if (item.studentOffer) {
          switch (selectedPeriod) {
            case 'MONTHLY':
              unitPrice = item.studentOffer.monthlyPrice || 0;
              break;
            case 'TRIMESTER':
              unitPrice = item.studentOffer.trimesterPrice || 0;
              break;
            case 'SEMESTER':
              unitPrice = item.studentOffer.semesterPrice || 0;
              break;
            case 'YEARLY':
              unitPrice = item.studentOffer.yearlyPrice || 0;
              break;
            default:
              unitPrice = item.studentOffer.monthlyPrice || 0;
          }
        }

        // Apply special multipliers for YEARLY period
        let subjectMultiplier = subjectCount;
        if (selectedPeriod === 'YEARLY') {
          switch (subjectCount) {
            case 1: subjectMultiplier = 1; break;
            case 2: subjectMultiplier = 1.5; break;
            case 3: subjectMultiplier = 2.25; break;
            case 4: subjectMultiplier = 3; break;
            default: subjectMultiplier = subjectCount;
          }
        } else {
          // For other periods, 4 subjects = 3.5x
          subjectMultiplier = subjectCount === 4 ? 3.5 : subjectCount;
        }
        const total = Math.floor(unitPrice * subjectMultiplier);
        const isFree = total === 0;
        const paid = typeof item.paidAmount === "number" ? Math.floor(item.paidAmount) : total;
        const showSavings = paid < total;

        // Format period label in French
        const periodLabels: Record<string, string> = {
          'MONTHLY': 'Mensuel',
          'TRIMESTER': 'Trimestriel',
          'SEMESTER': 'Semestriel',
          'YEARLY': 'Annuel'
        };
        const periodLabel = periodLabels[selectedPeriod] || selectedPeriod;

        return {
          ...item,
          id: item.id,
          nom: item.studentName || "Inconnu",
          offerType: item.studentOffer?.title || "Type inconnu",
          status: item.status,
          createdAt: item.requestDate,
          startDate: item.startDate,
          endDate: item.endDate,
          paymentImageUrl: item.paymentImageUrl || "#",
          period: periodLabel,
          selectedPeriodCode: selectedPeriod,
          subjects: (isFree || item.studentOffer?.allSubjects)
            ? `Toutes les matières`
            : `${subjectCount} matière${subjectCount > 1 ? 's' : ''}`,
          totalPrice: isFree
            ? "Gratuit"
            : showSavings
              ? `${paid} TND (au lieu de ${total} TND)`
              : `${paid} TND`,
          paidAmount: paid,
          baseAmount: total,
        };
      });
      setData(formattedData);
      setOriginalData(formattedData);
      setStudentRequestsToIDB('studentRequests', formattedData, now);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (originalData.length === 0) return;

    let filteredData = [...originalData];

    if (searchName.trim()) {
      const searchTerm = searchName.toLowerCase().trim();
      filteredData = filteredData.filter((item) =>
        item.nom.toLowerCase().includes(searchTerm)
      );
    }

    if (selectedStatusFilter !== "all") {
      filteredData = filteredData.filter(
        (item) => item.status === selectedStatusFilter
      );
    }

    if (dateFilter !== "none") {
      filteredData.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        let comparison =
          dateFilter === "asc"
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();

        if (comparison === 0) {
          comparison = dateFilter === "asc" ? a.id - b.id : b.id - a.id;
        }

        if (comparison === 0 && a.startDate && b.startDate) {
          const startA = new Date(a.startDate);
          const startB = new Date(b.startDate);
          comparison =
            dateFilter === "asc"
              ? startA.getTime() - startB.getTime()
              : startB.getTime() - startA.getTime();
        }

        return comparison;
      });
    }

    setData(filteredData);
  }, [searchName, selectedStatusFilter, dateFilter, originalData]);

  const handleResetFilters = () => {
    setSearchName("");
    setSelectedStatusFilter("all");
    setDateFilter("none");
    setData(originalData);
  };

  const handleActionClick = (
    row: RequestData,
    action: string,
    status: "ACCEPTED" | "REJECTED"
  ) => {
    setSelectedRow(row);
    setConfirmAction(action);
    setOpenConfirmDialog(true);
    setSelectedStatus(status);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setSelectedRow(null);
  };

  const handleActionConfirm = async () => {
    if (!selectedRow) return;
    setLoading(true);
    try {
      await respondOfferService(selectedRow.id, slectedStatus);
      // Clear cache before fetching new data
      await clearStudentRequestsCache();
      await fetchData();
      setOpenConfirmDialog(false);
      setSelectedRow(null);
    } catch (e) {
      console.error("Error responding to offer:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdjustDialog = (
    row: RequestData,
    forcedStatus?: "ACCEPTED" | "REJECTED"
  ) => {
    setSelectedRow(row);
    const todayStr = getTodayString();
    const defaultStatus =
      forcedStatus ??
      (row.status === "REJECTED" ? "REJECTED" : "ACCEPTED");
    const defaultStartDate =
      toDateInputValue(row.startDate) || toDateInputValue(row.createdAt) || todayStr;
    const defaultEndDate =
      toDateInputValue(row.endDate) ||
      (defaultStatus === "ACCEPTED"
        ? addPeriodToDate(defaultStartDate, row.selectedPeriodCode)
        : "");

    setAdjustPayload({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      status: defaultStatus,
    });
    setOpenAdjustDialog(true);
  };

  const handleCloseAdjustDialog = () => {
    setOpenAdjustDialog(false);
    setSelectedRow(null);
    setAdjustPayload({ startDate: "", endDate: "", status: "ACCEPTED" });
  };

  const handleOpenImageModal = (url: string) => {
    setCurrentImageUrl(url);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setCurrentImageUrl("");
  };

  const handleAdjustConfirm = async () => {
    if (!selectedRow) return;
    const isAccepted = adjustPayload.status === "ACCEPTED";
    let effectiveStartDate = adjustPayload.startDate;
    let effectiveEndDate = adjustPayload.endDate;

    if (isAccepted) {
      if (!effectiveStartDate) {
        effectiveStartDate =
          toDateInputValue(selectedRow.startDate) ||
          toDateInputValue(selectedRow.createdAt) ||
          getTodayString();
      }
      if (!effectiveEndDate) {
        effectiveEndDate = addPeriodToDate(effectiveStartDate, selectedRow.selectedPeriodCode);
      }
      if (!effectiveStartDate || !effectiveEndDate) {
        alert("Veuillez renseigner les dates de debut et de fin.");
        return;
      }
    }

    if (
      effectiveStartDate &&
      effectiveEndDate &&
      new Date(effectiveEndDate) < new Date(effectiveStartDate)
    ) {
      alert("La date de fin doit etre posterieure a la date de debut.");
      return;
    }
    try {
      setSavingAdjust(true);
      await adjustStudentOfferRequestService(selectedRow.id, {
        startDate: effectiveStartDate || null,
        endDate: effectiveEndDate || null,
        status: adjustPayload.status,
      });
      await clearStudentRequestsCache();
      await fetchData();
      handleCloseAdjustDialog();
    } catch (e) {
      console.error("Error updating student request:", e);
    } finally {
      setSavingAdjust(false);
    }
  };

  const renderActions = (row: TableData) => {
    const requestRow = row as RequestData;
    return (
      <div className="flex items-center justify-center space-x-2">
        {requestRow.status === "PENDING" ? (
          <>
            <button
              className="px-3 py-1 text-white rounded-full bg-primary_bg"
              onClick={() => handleActionClick(requestRow, "Accepted", "ACCEPTED")}
            >
              Accept
            </button>
            <button
              onClick={() => handleActionClick(requestRow, "Rejected", "REJECTED")}
              className="px-3 py-1 text-white rounded-full bg-red"
            >
              Reject
            </button>
          </>
        ) : (
          <>
            <span
              className={`px-3 py-1 text-white rounded-full ${requestRow.status === "ACCEPTED" ? "bg-primary_bg" : "bg-red"}`}
            >
              {requestRow.status === "ACCEPTED" ? "Accepted" : "Rejected"}
            </span>
            {/* <button
              className="px-3 py-1 text-white rounded-full bg-primary"
              onClick={() => handleOpenAdjustDialog(requestRow)}
            >
              Mettre a jour
            </button> */}
          </>
        )}
      </div>
    );
  };

  const renderStatus = (row: TableData) => {
    const requestRow = row as RequestData;
    return (
      <div className="flex items-center justify-center space-x-2">
        <a
          className="px-3 py-1 text-white rounded-full bg-text"
          href={requestRow.paymentImageUrl}
          target="_blank"
          rel="noreferrer"
        >
          Voir le Doc
        </a>
      </div>
    );
  };


  // On refresh, clear cache and fetch new data
  const handleRefresh = async () => {
    setLoading(true);
    await clearStudentRequestsCache();
    await fetchData();
  };

  return (
    <div className="w-full min-h-screen bg-[#F8FAFB] p-4 lg:p-10 animate-in fade-in duration-700">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div>
           <h1 className="text-3xl md:text-4xl font-black text-title tracking-tight uppercase">
             Demandes <span className="text-primary italic">Élèves</span>
           </h1>
           <p className="text-[11px] font-bold text-text/40 uppercase tracking-[0.4em] mt-2 ml-1">
             Gestion des inscriptions et validations
           </p>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-3 px-6 py-4 bg-white text-title font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 group"
        >
          <RefreshIcon className={`text-primary text-sm ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
          <span>Actualiser les données</span>
        </button>
      </div>

      {/* Stats Quick View (Mock or real) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          {[
            { label: 'En attente', count: data.filter(d => d.status === 'PENDING').length, color: 'text-amber-500', bg: 'bg-amber-50' },
            { label: 'Acceptées', count: data.filter(d => d.status === 'ACCEPTED').length, color: 'text-emerald-500', bg: 'bg-emerald-50' },
            { label: 'Rejetées', count: data.filter(d => d.status === 'REJECTED').length, color: 'text-rose-500', bg: 'bg-rose-50' },
            { label: 'Total', count: data.length, color: 'text-blue-500', bg: 'bg-blue-50' },
          ].map((stat, i) => (
            <div key={i} className="bg-white/80 backdrop-blur-md p-5 rounded-3xl border border-white shadow-sm flex items-center justify-between group hover:shadow-lg transition-all">
                <div>
                   <p className="text-[10px] font-black text-text/40 uppercase tracking-widest mb-1">{stat.label}</p>
                   <p className={`text-2xl font-black ${stat.color}`}>{stat.count}</p>
                </div>
                <div className={`w-12 h-12 ${stat.bg} rounded-2xl flex items-center justify-center`}>
                    <div className={`w-2 h-2 rounded-full ${stat.color.replace('text', 'bg')} ${stat.label === 'En attente' ? 'animate-pulse' : ''}`}></div>
                </div>
            </div>
          ))}
      </div>

      {/* Filters Section */}
      <div className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white shadow-xl mb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-title uppercase tracking-widest ml-2">Recherche</label>
            <TextField
              fullWidth
              placeholder="Nom de l'élève..."
              variant="outlined"
              size="small"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              InputProps={{
                sx: { borderRadius: '1.25rem', bgcolor: '#F9FAFB', '& fieldset': { border: 'none' }, height: '54px', fontWeight: 800, fontSize: '13px' }
              }}
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-title uppercase tracking-widest ml-2">Filtrer par statut</label>
            <FormControl fullWidth size="small">
              <Select
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value as string)}
                sx={{ borderRadius: '1.25rem', bgcolor: '#F9FAFB', '& fieldset': { border: 'none' }, height: '54px', fontWeight: 800, fontSize: '13px' }}
              >
                <MenuItem value="all">Tous les statuts</MenuItem>
                <MenuItem value="PENDING">En attente</MenuItem>
                <MenuItem value="ACCEPTED">Accepté</MenuItem>
                <MenuItem value="REJECTED">Rejeté</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-title uppercase tracking-widest ml-2">Trier par date</label>
            <FormControl fullWidth size="small">
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as "asc" | "desc" | "none")}
                sx={{ borderRadius: '1.25rem', bgcolor: '#F9FAFB', '& fieldset': { border: 'none' }, height: '54px', fontWeight: 800, fontSize: '13px' }}
              >
                <MenuItem value="none">Aucun tri</MenuItem>
                <MenuItem value="asc">Plus ancien</MenuItem>
                <MenuItem value="desc">Plus récent</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleResetFilters}
              className="w-full h-[54px] bg-primary/10 text-primary font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-primary hover:text-white transition-all duration-500"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Main Content: Card-based Request Board */}
      <div className="min-h-[500px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[500px] space-y-4">
             <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
             <p className="text-[10px] font-black text-text/40 uppercase tracking-widest">Synchronisation en cours...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white/40 backdrop-blur-md rounded-[3rem] border border-white shadow-2xl flex flex-col items-center justify-center h-[500px] text-center px-10">
             <div className="w-24 h-24 bg-gray-50 rounded-[2rem] flex items-center justify-center mb-6">
                <RefreshIcon sx={{ fontSize: 40, color: '#D1D5DB' }} />
             </div>
             <h3 className="text-xl font-black text-title uppercase mb-2">Aucune demande trouvée</h3>
             <p className="text-sm text-text/50 max-w-md">Nous n'avons trouvé aucune demande correspondant à vos critères de recherche.</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {data.map((req) => {
               const statusConfig: any = {
                'PENDING': { label: 'En attente', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', icon: faClock },
                'ACCEPTED': { label: 'Accepté', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: faCheck },
                'REJECTED': { label: 'Rejeté', color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100', icon: faTimes }
               };
               const config = statusConfig[req.status] || statusConfig['PENDING'];

               return (
                  <div key={req.id} className="group relative bg-white/70 hover:bg-white backdrop-blur-md p-6 md:p-8 rounded-[3rem] border border-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
                     <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-8">
                        
                        {/* 1. Student Info Section */}
                        <div className="flex items-center gap-6 min-w-[280px]">
                           <div className="relative">
                              <div className="w-16 h-16 md:w-20 md:h-20 rounded-[2rem] bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary font-black text-xl md:text-2xl border border-primary/10 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700">
                                {req.nom.substring(0, 2).toUpperCase()}
                              </div>
                              <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-lg border-2 border-white flex items-center justify-center shadow-lg ${config.bg.replace('bg-', 'bg-')}`}>
                                 <FontAwesomeIcon icon={config.icon} className={`text-[8px] ${config.color}`} />
                              </div>
                           </div>
                           <div>
                              <h4 className="text-lg md:text-xl font-black text-title uppercase tracking-tighter leading-tight mb-1 group-hover:text-primary transition-colors cursor-default">
                                {req.nom}
                              </h4>
                              <div className="flex items-center gap-4">
                                 <div className="flex items-center gap-2 text-[9px] font-bold text-text/30 uppercase tracking-widest">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-primary/30" />
                                    <span>{new Date(req.createdAt).toLocaleDateString()}</span>
                                 </div>
                                 <div className="w-1 h-1 rounded-full bg-slate-200"></div>
                                 <span className="text-[9px] font-black text-primary/60 uppercase tracking-widest">ID: #{req.id}</span>
                              </div>
                           </div>
                        </div>

                        {/* 2. Details Flow: Fluid & Responsive */}
                        <div className="flex-1 flex flex-wrap items-center gap-x-10 gap-y-6 xl:px-10 py-8 xl:py-0 border-y xl:border-y-0 xl:border-x border-gray-100/50">
                           <div className="space-y-1.5 min-w-[130px]">
                              <p className="text-[9px] font-black text-text/20 uppercase tracking-[0.2em]">Offre Académique</p>
                              <p className="text-[12px] font-black text-title uppercase truncate">{req.offerType}</p>
                           </div>
                           <div className="space-y-1.5 min-w-[110px]">
                              <p className="text-[9px] font-black text-text/20 uppercase tracking-[0.2em]">Volume</p>
                              <div className="flex items-center gap-2">
                                 <FontAwesomeIcon icon={faFileAlt} className="text-[10px] text-primary/30" />
                                 <p className="text-[12px] font-black text-title uppercase">{req.subjects}</p>
                              </div>
                           </div>
                           <div className="space-y-1.5 min-w-[100px]">
                              <p className="text-[9px] font-black text-text/20 uppercase tracking-[0.2em]">Période</p>
                              <p className="text-[12px] font-black text-title uppercase tracking-tighter italic">{req.period}</p>
                           </div>
                           <div className="space-y-1.5 min-w-[150px]">
                              <p className="text-[9px] font-black text-text/20 uppercase tracking-[0.2em]">Montant</p>
                              <div className="flex items-center gap-2 text-primary">
                                 <FontAwesomeIcon icon={faCoins} className="text-sm opacity-30" />
                                 <p className="text-[14px] font-black tracking-tight leading-none">{req.totalPrice}</p>
                              </div>
                           </div>
                        </div>

                        {/* 3. Actions & Status Section */}
                        <div className="flex items-center flex-wrap gap-4 min-w-fit">
                            {/* Details Document Button */}
                            <button 
                              onClick={() => handleOpenImageModal(req.paymentImageUrl)}
                              className="flex items-center gap-3 px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl group/btn hover:bg-title hover:border-title transition-all duration-300 cursor-pointer"
                            >
                               <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs text-text/40 group-hover/btn:text-white transition-colors" />
                               <span className="text-[10px] font-black text-title/60 uppercase tracking-widest group-hover/btn:text-white">Justificatif</span>
                            </button>

                           {req.status === 'PENDING' ? (
                             <div className="flex items-center gap-2">
                                <button 
                                  onClick={() => handleOpenAdjustDialog(req, "ACCEPTED")}
                                  className="h-14 px-6 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center gap-2"
                                >
                                  <FontAwesomeIcon icon={faPen} className="text-[10px]" />
                                  <span>Dates & Validation</span>
                                </button>
                                <button 
                                  onClick={() => handleActionClick(req, "Rejeter", "REJECTED")}
                                  className="h-14 px-6 bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-rose-200 hover:shadow-rose-400 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center gap-2"
                                >
                                  <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                                  <span>Rejeter</span>
                                </button>
                             </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                 <div className={`h-11 px-5 ${config.bg} ${config.color} border ${config.border} rounded-xl inline-flex items-center gap-2`}>
                                    <div className={`w-1.5 h-1.5 rounded-full ${config.color.replace('text', 'bg')}`}></div>
                                    <span className="text-[9px] font-black uppercase tracking-widest">{config.label}</span>
                                 </div>
                                 <button
                                   onClick={() => handleOpenAdjustDialog(req)}
                                   className="h-11 px-4 bg-white border border-gray-100 text-title text-[9px] font-black uppercase tracking-widest rounded-xl hover:border-primary hover:text-primary transition-all duration-300 flex items-center gap-2"
                                 >
                                   <FontAwesomeIcon icon={faPen} className="text-[10px]" />
                                   <span>Modifier dates</span>
                                 </button>
                              </div>
                            )}
                        </div>
                     </div>
                  </div>
               );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {openAdjustDialog && (
          <Modal
            open={openAdjustDialog}
            onClose={handleCloseAdjustDialog}
            className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
            closeAfterTransition
          >
            <motion.div 
              className="w-full max-w-lg bg-white rounded-[3rem] shadow-3xl outline-none border border-white/20 overflow-hidden"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
               {/* Modal Header */}
               <div className="bg-slate-50 p-8 flex items-center gap-5 border-b border-gray-100">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-xl shadow-inner">
                    <FontAwesomeIcon icon={faPen} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-title uppercase tracking-tighter leading-none italic">Mise à jour</h3>
                    <p className="text-[10px] font-bold text-text/30 uppercase tracking-[0.3em] mt-2">Abonnement & Calendrier</p>
                  </div>
               </div>

               <div className="p-8 lg:p-10 space-y-8">
                  {/* Student Reference */}
                  <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-2xl border border-primary/10">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-primary font-black shadow-sm">
                      {selectedRow?.nom.charAt(0).toUpperCase() || "S"}
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-primary/40 uppercase tracking-widest">Élève Référent</p>
                        <p className="text-sm font-black text-title uppercase">{selectedRow?.nom}</p>
                    </div>
                  </div>

                  {/* Dates Section */}
                  <div className="grid grid-cols-2 gap-6">
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-text/40 uppercase tracking-[0.2em] ml-2">Date de début</label>
                        <TextField
                          fullWidth
                          type="date"
                          value={adjustPayload.startDate}
                          disabled={adjustPayload.status === "REJECTED" || savingAdjust}
                          onChange={(e) => setAdjustPayload(p => ({ ...p, startDate: e.target.value }))}
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              bgcolor: '#F9FAFB', 
                              borderRadius: '1.25rem',
                              '& fieldset': { border: 'none' },
                              '&:hover fieldset': { border: 'none' },
                              '&.Mui-focused fieldset': { border: '2px solid #10b981' },
                              fontWeight: 800,
                              fontSize: '13px'
                            } 
                          }}
                        />
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-text/40 uppercase tracking-[0.2em] ml-2">Date de fin</label>
                        <TextField
                          fullWidth
                          type="date"
                          value={adjustPayload.endDate}
                          disabled={adjustPayload.status === "REJECTED" || savingAdjust}
                          onChange={(e) => setAdjustPayload(p => ({ ...p, endDate: e.target.value }))}
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              bgcolor: '#F9FAFB', 
                              borderRadius: '1.25rem',
                              '& fieldset': { border: 'none' },
                              '&:hover fieldset': { border: 'none' },
                              '&.Mui-focused fieldset': { border: '2px solid #10b981' },
                              fontWeight: 800,
                              fontSize: '13px'
                            } 
                          }}
                        />
                     </div>
                  </div>

                  {/* Status Selection */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-text/40 uppercase tracking-[0.2em] ml-2">Décision Administrative</label>
                    <FormControl fullWidth>
                      <Select
                        value={adjustPayload.status}
                        disabled={savingAdjust}
                        onChange={(e) => setAdjustPayload(p => ({ ...p, status: e.target.value as any }))}
                        sx={{ 
                          bgcolor: '#F9FAFB', 
                          borderRadius: '1.25rem',
                          '& fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: '2px solid #10b981' },
                          fontWeight: 800,
                          fontSize: '13px',
                          height: '56px'
                        }}
                      >
                        <MenuItem value="ACCEPTED" className="font-bold text-emerald-600">ACCEPTER L'INSCRIPTION</MenuItem>
                        <MenuItem value="REJECTED" className="font-bold text-rose-600">REJETER LA DEMANDE</MenuItem>
                      </Select>
                    </FormControl>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4 pt-4">
                     <button 
                       onClick={handleCloseAdjustDialog}
                       disabled={savingAdjust}
                       className="flex-1 h-14 bg-slate-100 text-slate-400 font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-slate-200 transition-all active:scale-95"
                     >
                       Annuler
                     </button>
                     <button 
                       onClick={handleAdjustConfirm}
                       disabled={savingAdjust}
                       className="flex-[2] h-14 bg-primary text-white font-black text-[10px] uppercase tracking-widest rounded-2xl shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                     >
                       {savingAdjust ? "Transmission..." : "Enregistrer les modifications"}
                     </button>
                  </div>
               </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>


      <AnimatePresence>
        {openConfirmDialog && (
          <Modal
            open={openConfirmDialog}
            onClose={handleCloseConfirmDialog}
            className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
            closeAfterTransition
          >
            <motion.div 
              className="w-full max-w-[450px] bg-white rounded-[3rem] p-10 shadow-3xl text-center outline-none border border-white/20"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className={`w-24 h-24 rounded-[2.5rem] flex items-center justify-center text-4xl mx-auto mb-8 border shadow-sm relative ${confirmAction === 'Accepter' ? 'bg-emerald-50 text-emerald-500 border-emerald-100' : 'bg-rose-50 text-rose-500 border-rose-100'}`}>
                <div className={`absolute inset-0 rounded-[2.5rem] animate-ping opacity-50 ${confirmAction === 'Accepter' ? 'bg-emerald-400/20' : 'bg-rose-400/20'}`}></div>
                <FontAwesomeIcon icon={confirmAction === 'Accepter' ? faCheck : faTimes} className="relative z-10" />
              </div>
              
              <h3 className="text-2xl font-black text-title uppercase tracking-tight mb-4 text-center">
                {confirmAction === 'Accepter' ? 'Valider' : 'Rejeter'} <span className={confirmAction === 'Accepter' ? 'text-emerald-500' : 'text-rose-500'}>la Demande</span>
              </h3>
              
              <p className="text-sm font-medium text-text/60 leading-relaxed mb-10 px-4 text-center">
                Voulez-vous <span className="font-black">{confirmAction.toLowerCase()}</span> la demande de <span className="text-[#05322B] font-black">{selectedRow?.nom}</span> ?
              </p>

              <div className="flex gap-4">
                <button
                  className="flex-1 h-16 bg-slate-50 text-text/40 font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-slate-100 transition-all duration-300 outline-none cursor-pointer"
                  onClick={handleCloseConfirmDialog}
                >
                  Annuler
                </button>
                <button
                  className={`flex-1 h-16 text-white font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-xl transition-all duration-300 outline-none cursor-pointer hover:-translate-y-1 ${confirmAction === 'Accepter' ? 'bg-emerald-500 shadow-emerald-500/20 hover:bg-emerald-600' : 'bg-rose-500 shadow-rose-500/20 hover:bg-rose-600'}`}
                  onClick={handleActionConfirm}
                >
                  Confirmer
                </button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Justification Image Lightbox Modal */}
      <Modal
        open={openImageModal}
        onClose={handleCloseImageModal}
        className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
        closeAfterTransition
      >
        <motion.div 
          className="relative max-w-5xl w-full bg-white rounded-[3rem] p-4 lg:p-8 shadow-3xl overflow-hidden outline-none"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
           <div className="flex items-center justify-between mb-6 px-4">
              <div className="flex items-center gap-4">
                 <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileAlt} />
                 </div>
                 <div>
                    <h3 className="text-lg font-black text-title uppercase tracking-tighter leading-none">Justificatif de Paiement</h3>
                    <p className="text-[10px] text-text/40 font-bold uppercase tracking-widest mt-1">Vérification de la transaction</p>
                 </div>
              </div>
              <button 
                onClick={handleCloseImageModal}
                className="w-11 h-11 rounded-full bg-slate-50 flex items-center justify-center text-text/40 hover:text-rose-500 hover:rotate-90 transition-all duration-500 cursor-pointer"
              >
                 <FontAwesomeIcon icon={faTimes} />
              </button>
           </div>

           <div className="bg-slate-50 rounded-[2rem] overflow-hidden flex items-center justify-center border-4 border-slate-50 group">
              <img 
                src={currentImageUrl} 
                alt="Payment Justification" 
                className="max-h-[70vh] w-auto object-contain shadow-2xl transition-transform duration-700 group-hover:scale-105"
              />
           </div>

           <div className="mt-6 flex justify-end px-4">
              <a 
                href={currentImageUrl} 
                target="_blank" 
                rel="noreferrer"
                className="h-12 px-6 bg-title text-white font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-primary transition-all flex items-center gap-2"
              >
                 <FontAwesomeIcon icon={faExternalLinkAlt} />
                 Ouvrir l'original
              </a>
           </div>
        </motion.div>
      </Modal>
    </div>
  );
};

export default Requests;
